import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { GitBranch, X } from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import type { ThreatModelCreationMode, ThreatModelVersion } from "../types";
import {
  SEMVER_HINT,
  compareSemver,
  normalizeSemver,
  suggestNextVersions,
} from "../utils/threatModelVersions";

interface ThreatModelVersionDialogProps {
  // Any accessible version of the family; used to list versions and as the
  // anchor for blank creation.
  anchorId: string;
  familyName: string;
  // Preselected copy source; defaults to the anchor when it is not archived.
  defaultSourceId?: string | undefined;
  onClose: () => void;
}

const MODES: {
  id: ThreatModelCreationMode;
  label: string;
  description: string;
}[] = [
  {
    id: "blank",
    label: "Start blank",
    description: "A new version with the default empty model.",
  },
  {
    id: "settings",
    label: "Copy settings",
    description:
      "Copies the saved scope, repositories, environment, assumptions, components, assets, data flows and prompt overrides.",
  },
  {
    id: "full",
    label: "Copy full content",
    description:
      "Copies everything from Copy settings plus findings with their statuses and generated reports.",
  },
];

export default function ThreatModelVersionDialog({
  anchorId,
  familyName,
  defaultSourceId,
  onClose,
}: ThreatModelVersionDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: versionsResp, isLoading } = useQuery(
    ["threat-model-versions", anchorId, "all"],
    () => apiService.getThreatModelVersions(anchorId, true),
  );
  const versions: ThreatModelVersion[] = useMemo(
    () => versionsResp?.data?.data ?? [],
    [versionsResp],
  );
  const sources = useMemo(
    () =>
      versions
        .filter((v) => !v.archived_at)
        .sort((a, b) => compareSemver(b.version, a.version)),
    [versions],
  );

  const [mode, setMode] = useState<ThreatModelCreationMode>(
    defaultSourceId ? "settings" : "blank",
  );
  const [sourceId, setSourceId] = useState<string>(defaultSourceId ?? "");
  const [version, setVersion] = useState("");
  const [summary, setSummary] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const effectiveSourceId =
    sourceId && sources.some((s) => s.id === sourceId)
      ? sourceId
      : (sources[0]?.id ?? "");
  const source = sources.find((s) => s.id === effectiveSourceId);
  const suggestionBase =
    mode === "blank" ? (sources[0]?.version ?? undefined) : source?.version;
  const suggestions = suggestNextVersions(
    suggestionBase,
    versions.map((v) => v.version),
  );

  const createMutation = useMutation(
    () => {
      const target = mode === "blank" ? anchorId : effectiveSourceId;
      return apiService.createThreatModelVersion(target, {
        version: version.trim(),
        mode,
        ...(summary.trim() && { summary: summary.trim() }),
      });
    },
    {
      onSuccess: (res) => {
        const created = res?.data?.data as ThreatModelVersion | undefined;
        queryClient.invalidateQueries(["threat-model-families"]);
        queryClient.invalidateQueries(["threat-model-versions"]);
        toast.success(`Version ${created?.version ?? ""} created`);
        onClose();
        if (created?.id) navigate(`/threat-modelling/${created.id}`);
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.error || "Failed to create the version";
        setErrors({ submit: msg });
      },
    },
  );

  const handleSubmit = () => {
    const next: Record<string, string> = {};
    if (!normalizeSemver(version)) next.version = SEMVER_HINT;
    if (mode !== "blank" && !effectiveSourceId) {
      next.source = "Select a version to copy from.";
    }
    setErrors(next);
    if (Object.keys(next).length === 0) createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              New version
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          <div>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Model
            </span>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {familyName}
            </p>
          </div>

          <div>
            <label
              htmlFor="tm-version"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Version
            </label>
            <input
              id="tm-version"
              className="input"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder={suggestions[0] ?? "1.0.0"}
            />
            {suggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                Suggestions:
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setVersion(s)}
                    className="px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {errors.version && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                {errors.version}
              </p>
            )}
          </div>

          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Starting point
            </legend>
            <div className="space-y-2">
              {MODES.map((m) => (
                <label
                  key={m.id}
                  className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  <input
                    type="radio"
                    name="tm-version-mode"
                    className="mt-1"
                    checked={mode === m.id}
                    onChange={() => setMode(m.id)}
                  />
                  <span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {m.label}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      {m.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          {mode !== "blank" && (
            <div>
              <label
                htmlFor="tm-version-source"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Source version
              </label>
              <select
                id="tm-version-source"
                className="input"
                value={effectiveSourceId}
                onChange={(e) => setSourceId(e.target.value)}
                disabled={isLoading}
              >
                {sources.length === 0 && (
                  <option value="">No versions available</option>
                )}
                {sources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.version} · {s.phase}
                  </option>
                ))}
              </select>
              {errors.source && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  {errors.source}
                </p>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="tm-version-summary"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Summary <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              id="tm-version-summary"
              className="input min-h-[72px]"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="What is this version for, or what changed?"
            />
          </div>

          <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-xs text-gray-600 dark:text-gray-300 space-y-1">
            {mode === "blank" && (
              <p>Nothing is copied. The version starts with default inputs.</p>
            )}
            {mode !== "blank" && (
              <p>
                Copies the <strong>saved</strong> content of{" "}
                {source ? source.version : "the source"}; unsaved edits in an
                open editor are not included.
              </p>
            )}
            {mode === "settings" && (
              <p>
                Findings, reports and run history are not copied. The version is
                ready for a fresh analysis.
              </p>
            )}
            {mode === "full" && (
              <p className="text-amber-700 dark:text-amber-300">
                Findings and reports are inherited from{" "}
                {source ? source.version : "the source"}; no new analysis has
                run for this version. Run history and active jobs are not
                copied.
              </p>
            )}
            <p>
              Product links are not copied, existing product pins stay unchanged
              and no analysis is started.
            </p>
          </div>

          {errors.submit && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {errors.submit}
            </p>
          )}
        </div>

        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-3 flex justify-end gap-2 rounded-b-lg">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={createMutation.isLoading}
            className="btn btn-primary disabled:opacity-50"
          >
            {createMutation.isLoading ? "Creating..." : "Create version"}
          </button>
        </div>
      </div>
    </div>
  );
}
